import { createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * Shared structural configuration (typography, shape, component defaults).
 * Colors live in the per-mode palette blocks below.
 */
const commonTheme: ThemeOptions = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 500 }
  },
  shape: { borderRadius: 8 },
  components: {
    // App-wide baseline: fixed-width digits everywhere (cleaner numeric columns
    // in a trading app) + slim, themed scrollbars. Palette-aware → both modes.
    MuiCssBaseline: {
      styleOverrides: (theme: any) => ({
        body: { fontVariantNumeric: 'tabular-nums' },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.mode === 'dark' ? '#30363D' : '#CBD5E1',
          borderRadius: 8,
          border: `2px solid ${theme.palette.background.default}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? '#3F4854' : '#B0BCC9',
        },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      }),
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        sizeSmall: { paddingTop: 4, paddingBottom: 4 },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' }
      }
    },
    // Flat, professional cards: 1px divider border + larger radius, no heavy
    // shadow. The border replaces elevation for separation in both themes.
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }: any) => ({
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 12,
        }),
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        title: { fontSize: '1rem', fontWeight: 700 },
        subheader: { fontSize: '0.78rem' },
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.75rem' },
        /* MUI scales a small chip's label to 0.8125em of the root, which landed
           at 9.92px — below the 11px floor. Pin it instead of inheriting the
           scale-down. */
        sizeSmall: { height: 22, fontSize: '0.75rem' },
        labelSmall: { fontSize: '0.6875rem' },
        label: { paddingLeft: 8, paddingRight: 8 },
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontVariantNumeric: 'tabular-nums' },
        head: ({ theme }: any) => ({
          fontWeight: 700,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: theme.palette.text.secondary,
          whiteSpace: 'nowrap',
          backgroundColor: theme.palette.background.paper,
        }),
      }
    },
    // Subtle row hover for scannability (light + dark via action.hover).
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }),
      }
    },
    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 350 },
      styleOverrides: {
        tooltip: ({ theme }: any) => ({
          fontSize: '0.74rem',
          lineHeight: 1.45,
          maxWidth: 340,
          padding: '6px 10px',
          backgroundColor: theme.palette.mode === 'dark' ? '#1F2733' : '#1A2332',
          color: '#FFFFFF',
        }),
        arrow: ({ theme }: any) => ({ color: theme.palette.mode === 'dark' ? '#1F2733' : '#1A2332' }),
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: '0.875rem' }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 700 },
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      }
    },
  }
};

export const lightTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'light',
    primary: {
      main:  '#12559F',   // matches colorTokens.light.neutral
      light: '#42A5F5',
      dark:  '#0D47A1',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main:  '#6B21A8',   // purple — accent / premium
      light: '#9C27B0',
      dark:  '#4A148C',
      contrastText: '#FFFFFF'
    },
    success: {
      main:  '#07593A',   // matches colorTokens.light.bull
      light: '#2E7D32',
      dark:  '#064E35',
      contrastText: '#FFFFFF'
    },
    error: {
      main:  '#991717',   // matches colorTokens.light.bear
      light: '#DC2626',
      dark:  '#7F1D1D',
      contrastText: '#FFFFFF'
    },
    warning: {
      main:  '#8A3F07',   // matches colorTokens.light.warn
      light: '#D97706',
      dark:  '#78350F',
      contrastText: '#FFFFFF'
    },
    info: {
      main:  '#12559F',   // matches colorTokens.light.neutral
      light: '#2196F3',
      dark:  '#0D47A1',
      contrastText: '#FFFFFF'
    },
    text: {
      primary:   '#1A2332',   // near-black with cool tint
      // Darkened from #546E7A (2026-09-03). 4.9:1 on white, but this is the app's default
      // muted text and it lands on tinted chips constantly -- 3.70-4.43:1 across the
      // Dashboard. #455A64 clears 4.97:1 on every background the live audit found.
      secondary: '#455A64'
    },
    divider: '#CBD5E1',
    background: {
      default: '#F0F4F8',     // soft blue-gray — not stark white, easy on eyes
      paper:   '#FFFFFF'
    },
    action: {
      hover:    'rgba(0,0,0,0.04)',
      selected: 'rgba(21,101,192,0.08)',
      disabled: 'rgba(0,0,0,0.26)'
    }
  }
});

export const darkTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'dark',
    primary: {
      main:  '#60A5FA',   // bright blue — high visibility on dark
      light: '#93C5FD',
      dark:  '#2563EB',
      contrastText: '#0D1117'
    },
    secondary: {
      main:  '#A78BFA',   // bright purple
      light: '#C4B5FD',
      dark:  '#7C3AED',
      contrastText: '#0D1117'
    },
    success: {
      main:  '#2DD4AA',   // bright teal-green
      light: '#6EE7CB',
      dark:  '#059669',
      contrastText: '#0D1117'
    },
    error: {
      main:  '#F47681',   // matches colorTokens.dark.bear
      light: '#FCA5A5',
      dark:  '#DC2626',
      contrastText: '#0D1117'
    },
    warning: {
      main:  '#FBBF24',   // bright amber
      light: '#FDE68A',
      dark:  '#D97706',
      contrastText: '#0D1117'
    },
    info: {
      main:  '#60A5FA',
      light: '#93C5FD',
      dark:  '#2563EB',
      contrastText: '#0D1117'
    },
    text: {
      primary:   '#E6EDF3',   // off-white (GitHub-dark style) — less strain than pure white
      // Raised twice on 2026-09-03. #8B949E measured 5.62:1 on the card surface and fine
      // there, but 3.06-4.16:1 on the tinted chips it actually lands on; #98A1AB only got
      // to 3.59. #B1BAC4 clears 4.79:1 on every background the live audit found, and still
      // reads as clearly secondary against primary #E6EDF3 (14.6:1 on paper).
      secondary: '#B1BAC4'
    },
    divider: '#30363D',
    background: {
      default: '#0D1117',     // deep near-black (GitHub-dark palette)
      paper:   '#161B22'      // slightly lighter card surface
    },
    action: {
      hover:    'rgba(255,255,255,0.06)',
      selected: 'rgba(96,165,250,0.12)',
      disabled: 'rgba(255,255,255,0.26)'
    }
  }
});
